import { useCallback, useEffect, useState } from "react";
import { useSDK } from "../lib/sdk-context";
import { brandApi } from "../lib/brand-api";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Dialog, DialogTitle } from "../components/ui/Dialog";
import { useToast } from "../components/ui/Toast";
import { displayLicenseName } from "../lib/display-name";

/** Coerce a Postgres numeric (which arrives as a string over JSON) to a number. */
function num(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

type Bucket = "auto_matched" | "needs_review" | "unmatched";
type Kind = "cm" | "wtb";

interface Candidate {
  licenseId: string;
  score: number;
  dba: string;
  legalName: string;
  address?: string;
  reason: string;
}
interface MatchedLicense {
  license_number: string;
  dba_name: string | null;
  legal_name: string | null;
  display_name: string | null;
  premise_street: string | null;
  premise_city: string | null;
}
interface Row {
  row_id: string;
  source_value: string | null;
  match_status: string;
  match_confidence: number | null;
  matched_license: MatchedLicense | null;
  candidates: Candidate[];
  private: Record<string, unknown>;
}

const BUCKETS: { key: Bucket; label: string }[] = [
  { key: "auto_matched", label: "Auto-matched" },
  { key: "needs_review", label: "Needs review" },
  { key: "unmatched", label: "Unmatched" },
];

export default function BrandReview({ slug }: { slug: string }) {
  const { sdk } = useSDK();
  const toast = useToast();
  const [kind, setKind] = useState<Kind>("cm");
  const [bucket, setBucket] = useState<Bucket>("auto_matched");
  const [rows, setRows] = useState<Row[]>([]);
  const [privLabels, setPrivLabels] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchFor, setSearchFor] = useState<Row | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandApi.get(
        `/api/brand/${encodeURIComponent(slug)}/${kind}?status=${bucket}&limit=100`
      );
      const data: Row[] = r.data || [];
      setRows(data);
      const labels = new Set<string>();
      data.forEach((row) => Object.keys(row.private || {}).forEach((k) => labels.add(k)));
      setPrivLabels([...labels].sort());
    } catch (err) {
      sdk?.reportError?.({ title: "Review load failed", description: (err as Error).message, severity: "medium" });
      toast.push(`Failed to load: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [slug, kind, bucket, sdk, toast]);

  useEffect(() => { load(); }, [load]);

  const resolve = async (row: Row, licenseId: string) => {
    try {
      await brandApi.post(`/api/brand/${encodeURIComponent(slug)}/${kind}/${row.row_id}/resolve`, { license_id: licenseId });
      toast.push("Match saved", "success");
      setSearchFor(null);
      load();
    } catch (err) {
      toast.push(`Resolve failed: ${(err as Error).message}`, "error");
    }
  };
  const unmatch = async (row: Row) => {
    try {
      await brandApi.post(`/api/brand/${encodeURIComponent(slug)}/${kind}/${row.row_id}/unmatch`, {});
      toast.push("Cleared match", "success");
      load();
    } catch (err) {
      toast.push(`Unmatch failed: ${(err as Error).message}`, "error");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Review — {slug}</h2>
        <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
          <button className={`px-3 py-1.5 text-sm ${kind === "cm" ? "bg-accent-500 text-white" : "bg-white text-gray-600"}`} onClick={() => setKind("cm")}>Customer Master</button>
          <button className={`px-3 py-1.5 text-sm ${kind === "wtb" ? "bg-accent-500 text-white" : "bg-white text-gray-600"}`} onClick={() => setKind("wtb")}>Where-to-Buy</button>
        </div>
      </div>

      <div className="flex gap-2">
        {BUCKETS.map((b) => (
          <button key={b.key} onClick={() => setBucket(b.key)}
            className={`px-3 py-1.5 rounded-full text-sm border ${bucket === b.key ? "border-accent-500 bg-accent-50 text-accent-700" : "border-gray-200 text-gray-600"}`}>
            {b.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-400 text-sm">No rows in this bucket.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Source value</th>
                {bucket === "auto_matched" && <><th className="px-4 py-2">Matched retailer</th><th className="px-4 py-2">Confidence</th></>}
                {bucket === "needs_review" && <th className="px-4 py-2">Candidates</th>}
                {bucket === "unmatched" && <th className="px-4 py-2">Search</th>}
                {privLabels.map((l) => <th key={l} className="px-4 py-2">{l}</th>)}
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.row_id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3 font-medium text-gray-800">{row.source_value || <span className="text-gray-400">(blank)</span>}</td>

                  {bucket === "auto_matched" && (
                    <>
                      <td className="px-4 py-3">
                        {row.matched_license ? (
                          <div>
                            <div className="text-gray-800">{displayLicenseName(row.matched_license.display_name, row.matched_license.dba_name, row.matched_license.legal_name)}</div>
                            <div className="text-xs text-gray-500">
                              {[row.matched_license.premise_street, row.matched_license.premise_city].filter(Boolean).join(", ")} · {row.matched_license.license_number}
                            </div>
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3"><Badge variant="auto">{(() => { const n = num(row.match_confidence); return n != null ? n.toFixed(3) : "—"; })()}</Badge></td>
                    </>
                  )}

                  {bucket === "needs_review" && (
                    <td className="px-4 py-3 space-y-1">
                      {(row.candidates || []).slice(0, 3).map((c) => (
                        <button key={c.licenseId} onClick={() => resolve(row, c.licenseId)}
                          className="block w-full text-left rounded border border-gray-200 px-2 py-1 hover:bg-accent-50">
                          <span className="text-gray-800">{c.dba || c.legalName}</span>
                          <span className="text-xs text-gray-500 ml-2">{(num(c.score) ?? 0).toFixed(2)} · {c.reason}</span>
                        </button>
                      ))}
                      <Button size="sm" variant="ghost" onClick={() => setSearchFor(row)}>Search for a different retailer</Button>
                    </td>
                  )}

                  {bucket === "unmatched" && (
                    <td className="px-4 py-3 space-y-1">
                      <Button size="sm" onClick={() => setSearchFor(row)}>Search retailer</Button>
                      <Button size="sm" variant="ghost" disabled title="coming soon">I don't see this retailer</Button>
                    </td>
                  )}

                  {privLabels.map((l) => (
                    <td key={l} className="px-4 py-3 text-gray-600">{String(row.private?.[l] ?? "")}</td>
                  ))}

                  <td className="px-4 py-3 space-x-1 whitespace-nowrap">
                    {bucket === "auto_matched" && (
                      <>
                        <Button size="sm" variant="primary" onClick={() => row.matched_license && resolve(row, row.matched_license.license_number)}>Confirm</Button>
                        <Button size="sm" onClick={() => setSearchFor(row)}>Change</Button>
                        <Button size="sm" variant="danger-ghost" onClick={() => unmatch(row)}>Reject</Button>
                      </>
                    )}
                    {bucket !== "auto_matched" && (
                      <Button size="sm" variant="ghost" onClick={() => unmatch(row)}>Mark unmatched</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {searchFor && (
        <SearchModal slug={slug} row={searchFor} onClose={() => setSearchFor(null)} onPick={(lic) => resolve(searchFor, lic)} />
      )}
    </div>
  );
}

function SearchModal({ slug, row, onClose, onPick }: {
  slug: string; row: Row; onClose: () => void; onPick: (licenseNumber: string) => void;
}) {
  const [q, setQ] = useState(row.source_value || "");
  const [results, setResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await brandApi.get(`/api/brand/${encodeURIComponent(slug)}/retailers/search?q=${encodeURIComponent(q)}&limit=20`);
        setResults(r.data || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, slug]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogTitle>Find a retailer for “{row.source_value}”</DialogTitle>
      <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search DBA / legal name…" className="mt-3" />
      <div className="mt-3 max-h-72 overflow-y-auto space-y-1">
        {searching && <p className="text-xs text-gray-400">Searching…</p>}
        {results.map((r) => (
          <button key={r.license_number} onClick={() => onPick(r.license_number)}
            className="block w-full text-left rounded border border-gray-200 px-3 py-2 hover:bg-accent-50">
            <div className="text-gray-800">{displayLicenseName(r.display_name, r.dba_name, r.legal_name)}</div>
            <div className="text-xs text-gray-500">{[r.premise_street, r.premise_city].filter(Boolean).join(", ")} · {r.license_number} · {r.status}</div>
          </button>
        ))}
        {!searching && q.trim().length >= 2 && results.length === 0 && (
          <p className="text-xs text-gray-400">No matches.</p>
        )}
      </div>
    </Dialog>
  );
}
